<template>
  <main class="container invite-landing text-center">
    <section class="invite-card" aria-live="polite">
      <div v-if="loading">
        <h1>Loading invite...</h1>
        <p>Checking your invite link.</p>
      </div>

      <div v-else-if="invalidInvite">
        <h1>Invite unavailable</h1>
        <p>This invite link is invalid or has expired.</p>
      </div>

      <div v-else-if="previewError">
        <h1>We couldn't load this invite</h1>
        <p>{{ previewError }}</p>
        <button class="btn btn-yellow" type="button" @click="loadPreview">Try again</button>
      </div>

      <div v-else-if="invite">
        <h1>You've been invited to join:</h1>
        <h2>{{ invite.listName }}</h2>

        <div v-if="loggedIn" class="mt-4">
          <p>Join this list to start taking part.</p>
          <p v-if="joinError" class="alert alert-danger" role="alert">{{ joinError }}</p>
          <button class="btn btn-yellow" type="button" :disabled="joining" @click="joinList">
            {{ joining ? "Joining list..." : "Join list" }}
          </button>
        </div>

        <div v-else class="mt-4">
          <p v-if="accountCreationAllowed">Sign up or log in to join this list.</p>
          <p v-else>Account creation is currently closed. If you already have an account, log in to join this list.</p>
          <router-link v-if="accountCreationAllowed" class="btn btn-yellow mr-2" :to="authRoute('Sign Up')">Sign up</router-link>
          <router-link class="btn btn-blue" :to="authRoute('Login')">Log in</router-link>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import InviteService from "@/services/InviteService";
import { accountCreationAllowed } from "@/config/accountCreation";

export default {
  name: "InviteLanding",
  data() {
    return {
      invite: null,
      loading: true,
      invalidInvite: false,
      previewError: "",
      joining: false,
      joinError: "",
    };
  },
  computed: {
    accountCreationAllowed() {
      return accountCreationAllowed;
    },
    loggedIn() {
      return this.$store.getters.loggedIn;
    },
    token() {
      return this.$route.params.token;
    },
  },
  watch: {
    token() {
      this.loadPreview();
    },
  },
  created() {
    this.loadPreview();
  },
  methods: {
    authRoute(name) {
      return {
        name,
        query: { redirect: this.$route.fullPath },
      };
    },
    async loadPreview() {
      this.loading = true;
      this.invite = null;
      this.invalidInvite = false;
      this.previewError = "";
      this.joinError = "";

      try {
        this.invite = await InviteService.getPreview(this.token);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.invalidInvite = true;
        } else {
          this.previewError = "Please check your connection and try again.";
        }
      } finally {
        this.loading = false;
      }
    },
    async joinList() {
      if (this.joining) {
        return;
      }

      this.joining = true;
      this.joinError = "";

      try {
        const membership = await InviteService.join(this.token);
        await this.$router.replace({ name: "List", params: { id: membership.listId } });
      } catch (error) {
        const status = error.response ? error.response.status : null;

        if (status === 401) {
          await this.$router.push(this.authRoute("Login"));
        } else if (status === 404) {
          this.invite = null;
          this.invalidInvite = true;
        } else if (status === 403) {
          this.joinError = (error.response.data && error.response.data.message) || "You do not have permission to join this list.";
        } else {
          this.joinError = "We couldn't join this list. Please try again.";
        }
      } finally {
        this.joining = false;
      }
    },
  },
};
</script>

<style scoped>
.invite-landing {
  min-height: calc(100vh - 136px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.invite-card {
  width: 100%;
  max-width: 620px;
  padding: 2rem;
  color: #f5f6f4;
  background-color: #6a72a0;
  border: solid #393d55 5px;
}

.invite-card h2 {
  color: #e8c547;
}
</style>
